// 장바구니 관련 타입 정의

import type { ApiMenuOptionGroup, MenuAvailabilityInfo } from '@/lib/api/types';
import type { ServerCart, ServerCartItem } from '@/lib/api/types';
import { normalizeSelectedOptionsForPayload } from '@/utils/selectedOptions';

export interface CartItem {
  id: string;
  name: string;
  description: string;
  price: number;
  originalPrice?: number;
  quantity: number;
  imageUrl?: string;
  category: string;
  restaurantId: string;
  restaurantName: string;
  customizations?: string[];
  menuItemId?: string; // 원본 메뉴 아이템 ID for option editing
  // 옵션 편집용 메타데이터 (#65: 하드코딩된 목업 데이터 제거)
  _originalOptions?: ApiMenuOptionGroup[]; // 원본 API 옵션 데이터
  /**
   * #280 보강: 가용성 정보 보존. 장바구니 편집 모달(MenuOptionModal 재사용)에서
   * "판매 시간" 행을 노출하려면 추가 시점의 availability를 보관해야 함.
   */
  _availability?: MenuAvailabilityInfo;
  selectedOptions?: Record<string, string[]>; // 선택된 옵션 (그룹ID → 옵션ID[])
  /**
   * #498: 입력형 옵션 (number_input/text_input/textarea) 사용자 입력 값.
   * key = optionGroup.id (string), value = 사용자 입력 문자열.
   * cart 편집 / sessionStorage 직렬화 / BE 주문 전송 모두에서 보존.
   */
  inputValues?: Record<string, string>;
  /** 서버 장바구니 아이템 ID (서버 동기화용) */
  serverCartItemId?: number;
}

// ==================== 서버↔클라이언트 어댑터 ====================

/**
 * 서버 장바구니 아이템을 클라이언트 CartItem으로 변환.
 *
 * #1128 (BE PR #1129): `serverItem.menu.is_available_at`을 `_availability`로 매핑하여
 *   서버 재수화 경로(로그인 직후·새로고침·다른 기기 진입)에서도 카트 편집 모달의
 *   "판매 시간" 행(#280)이 유지되도록 보장.
 */
export function serverCartItemToClientItem(
  serverItem: ServerCartItem,
  storeName: string,
  storeId: string
): CartItem {
  return {
    id: `server-${serverItem.id}`,
    name: serverItem.menu.name,
    description: serverItem.menu.description || '',
    price: serverItem.unit_price + serverItem.options_price,
    quantity: serverItem.quantity,
    imageUrl: serverItem.menu.image_url || undefined,
    category: '',
    restaurantId: storeId,
    restaurantName: storeName,
    menuItemId: String(serverItem.menu_id),
    serverCartItemId: serverItem.id,
    selectedOptions: normalizeSelectedOptionsForPayload(serverItem.selected_options),
    // #502 (admin#1500): 서버 cart 재수화 시 입력형 옵션 사용자 입력 값 보존.
    inputValues: serverItem.input_values ?? undefined,
    _availability: serverItem.menu.is_available_at ?? undefined,
  };
}

/**
 * 서버 장바구니 전체를 클라이언트 CartItem 배열로 변환
 */
export function serverCartToClientItems(serverCart: ServerCart): CartItem[] {
  const storeName = serverCart.store?.name ?? '';
  return serverCart.cart_items.map((item) =>
    serverCartItemToClientItem(
      item,
      storeName,
      String(serverCart.store_id)
    )
  );
}
